import { ArrowLeftOutlined, PlusOutlined } from '@ant-design/icons';
import type { CreateInvoiceDraftInput, UpdateInvoiceDraftInput } from '@financiar/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { App, Alert, Button, Card, Col, DatePicker, Divider, Empty, Form, Input, Popconfirm, Row, Select, Space, Spin, Typography } from 'antd';
import dayjs from 'dayjs';
import type { Dayjs } from 'dayjs';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, useBlocker, useNavigate, useParams } from 'react-router';
import { getApiError } from '../../api/client';
import type { Client, InvoiceDetail } from '../../api/types';
import { applyServerErrors } from '../../lib/forms';
import { formatNumber } from '../../lib/format';
import { fetchClienti } from '../clienti/api';
import { ClientFormDrawer } from '../clienti/ClientFormDrawer';
import { fetchFirma } from '../firme/api';
import { useFirmaCurenta } from '../firme/FirmaCurentaContext';
import { fetchProduse, fetchVatRates } from '../produse/api';
import { fetchSeries } from '../serii/api';
import { createFacturaDraft, fetchFactura, fetchSeriesPreview, issueFactura, updateFacturaDraft } from './api';
import { emptyLine, LineItemsEditor, toLinePayload, type VatCategoryOption } from '../../components/LineItemsEditor';
import { computeLiveTotals, makeVatResolver, VAT_CATEGORY_LABELS, VAT_CATEGORY_ORDER, type LineFormValue } from './totals';
import { TotalsPanel } from './TotalsPanel';

const { Title, Text } = Typography;

/** UN/CEFACT 4461, restricted to the three means this app supports (see
 * `PAYMENT_MEANS_CODES` in the shared invoice schema). */
const PAYMENT_MEANS_OPTIONS = [
  { value: '30', label: 'Transfer bancar' },
  { value: '10', label: 'Numerar' },
  { value: '42', label: 'Cont bancar' },
];

/** A firm realistically has tens of products, not thousands: one page covers
 * the line editor's Select, which then filters client-side (so a product
 * already on a loaded draft always renders with its real name instead of a
 * bare id). 100 is the server's hard cap (`paginationQuerySchema`), and the
 * same pragmatic call as `fetchFirme`'s pageSize 100. */
const PRODUCTS_PAGE_SIZE = 100;

interface FacturaFormValues {
  seriesId?: string;
  clientId?: string;
  dueDate?: Dayjs | null;
  supplyDate?: Dayjs | null;
  paymentMeansCode?: string;
  bankAccountId?: string;
  notes?: string;
  lines: LineFormValue[];
}

/** A `@db.Date` arrives as a full ISO timestamp; slicing the date part is
 * exact, while `dayjs(iso)` would re-interpret UTC midnight in the local
 * zone. */
function dayjsFromIsoDate(value: string | null | undefined): Dayjs | undefined {
  return value ? dayjs(value.slice(0, 10)) : undefined;
}

/**
 * The draft editor — `/facturi/noua` and `/facturi/:id/editare`, the same
 * component for both (creating simply means there is no id yet).
 *
 * The totals shown while typing are computed by the SHARED
 * `computeInvoiceTotals` over VAT rates resolved exactly the way the server
 * resolves them (see totals.ts), so "what you saw" and "what got saved" are
 * the same numbers by construction, not by convention.
 */
export function FacturaEditorPage() {
  const { id: routeId } = useParams();
  const navigate = useNavigate();
  const { message, modal } = App.useApp();
  const queryClient = useQueryClient();
  const { firmaId, isLoading: isLoadingFirme, setFirmaId } = useFirmaCurenta();
  const [form] = Form.useForm<FacturaFormValues>();

  const [invoiceId, setInvoiceId] = useState<string | null>(routeId ?? null);
  const [clientSearch, setClientSearch] = useState('');
  const [clientDrawerOpen, setClientDrawerOpen] = useState(false);
  const [extraClients, setExtraClients] = useState<Client[]>([]);
  const [issueConfirmOpen, setIssueConfirmOpen] = useState(false);
  const [isDirty, setIsDirty] = useState(false);

  // Dirty state is mirrored into a ref because the navigation blocker and the
  // firm-switch guard both need to read it SYNCHRONOUSLY, in the same tick a
  // successful save clears it — a `useState` value would still be `true` in
  // the closure the blocker was registered with and would pop a bogus
  // "unsaved changes" modal on the way to the view page.
  const dirtyRef = useRef(false);
  const markDirty = useCallback((value: boolean) => {
    dirtyRef.current = value;
    setIsDirty(value);
  }, []);

  const syncedForRef = useRef<string | null>(null);
  const issueTargetRef = useRef<string | null>(null);

  useEffect(() => {
    if (routeId && routeId !== invoiceId) setInvoiceId(routeId);
  }, [routeId, invoiceId]);

  const { data: invoice, isLoading: isLoadingInvoice } = useQuery({
    queryKey: ['invoices', 'detail', invoiceId],
    queryFn: () => fetchFactura(invoiceId as string),
    enabled: !!invoiceId,
  });

  // An existing draft is edited in ITS OWN firm's context (deep link from
  // another firm included); a new one belongs to the current firm.
  const companyId = invoice?.companyId ?? firmaId;

  const { data: firmDetail, isLoading: isLoadingFirm } = useQuery({
    queryKey: ['firms', 'detail', companyId],
    queryFn: () => fetchFirma(companyId as string),
    enabled: !!companyId,
  });

  /**
   * Read from the firm DETAIL response for this exact company — never from
   * the FirmaCurenta list. That list is one page of ACTIVE firms only, so a
   * draft belonging to a deactivated firm (or to any firm past the first
   * page) would miss the lookup and fall back to `false`, silently flipping
   * the editor into neplătitor mode: VAT columns gone, an Alert that lies,
   * and live totals computed at 0% while the server still resolves the real
   * rates at issue time. `FirmsService.findOne()` derives this field the same
   * way `findAll()` does, so the detail row is authoritative and always
   * present for the company actually being invoiced.
   *
   * Everything that depends on it is gated on `isReady` below (which waits
   * for this query), so "not loaded yet" never renders as "neplătitor".
   */
  const isVatPayerToday = firmDetail?.isVatPayerToday ?? false;

  const { data: series, isLoading: isLoadingSeries } = useQuery({
    queryKey: ['series', companyId],
    queryFn: () => fetchSeries(companyId as string),
    enabled: !!companyId,
  });

  const { data: productsPage, isLoading: isLoadingProducts } = useQuery({
    queryKey: ['products', companyId, { page: 1, pageSize: PRODUCTS_PAGE_SIZE }],
    queryFn: () => fetchProduse({ companyId: companyId as string, page: 1, pageSize: PRODUCTS_PAGE_SIZE }),
    enabled: !!companyId,
  });

  const { data: clientsPage } = useQuery({
    queryKey: ['clients', companyId, { page: 1, pageSize: 20, search: clientSearch }],
    queryFn: () =>
      fetchClienti({ companyId: companyId as string, page: 1, pageSize: 20, search: clientSearch || undefined }),
    enabled: !!companyId,
  });

  const { data: vatRates } = useQuery({
    queryKey: ['vat-rates'],
    queryFn: fetchVatRates,
    staleTime: 60 * 60 * 1000,
  });

  const seriesOptions = useMemo(
    () => (series ?? []).filter((s) => s.docType === 'INVOICE' && s.isActive).map((s) => ({ value: s.id, label: s.prefix })),
    [series],
  );

  const bankOptions = useMemo(
    () =>
      (firmDetail?.bankAccounts ?? []).map((account) => ({
        value: account.id,
        label: `${account.bankName} — ${account.iban}${account.isDefault ? ' (implicit)' : ''}`,
      })),
    [firmDetail],
  );

  const selectedSeriesId = Form.useWatch('seriesId', form);
  const watchedLines = Form.useWatch('lines', form) as LineFormValue[] | undefined;

  const { data: preview } = useQuery({
    queryKey: ['series', 'preview', selectedSeriesId],
    queryFn: () => fetchSeriesPreview(selectedSeriesId as string),
    enabled: !!selectedSeriesId,
  });

  const vatCategoryOptions: VatCategoryOption[] = useMemo(() => {
    // `/vat-rates` hasn't answered yet — every category is enabled with a
    // neutral label. Marking them "fără cotă valabilă"/disabled here (as the
    // branch below does once the data has actually arrived) would be a false
    // alarm: there is no evidence yet that any category is actually missing
    // a rate, only that the request hasn't finished.
    if (vatRates === undefined) {
      return VAT_CATEGORY_ORDER.map((category) => ({ value: category, label: VAT_CATEGORY_LABELS[category] }));
    }
    const rateByCategory = new Map(vatRates.map((rate) => [rate.category, rate.ratePercent]));
    return VAT_CATEGORY_ORDER.map((category) => {
      const rate = rateByCategory.get(category);
      return rate
        ? { value: category, label: `${VAT_CATEGORY_LABELS[category]} (${formatNumber(rate)}%)` }
        : // No rate in effect today — the server would answer a save with a
          // 409 VAT_RATE_NOT_FOUND, so the option is shown but unusable.
          { value: category, label: `${VAT_CATEGORY_LABELS[category]} (fără cotă valabilă)`, disabled: true };
    });
  }, [vatRates]);

  const liveTotals = useMemo(
    () => computeLiveTotals(watchedLines, makeVatResolver(isVatPayerToday, vatRates ?? []), vatRates === undefined),
    [watchedLines, isVatPayerToday, vatRates],
  );
  const computedLines = liveTotals.status === 'ok' ? liveTotals.totals.lines : null;

  const clientOptions = useMemo(() => {
    const options = new Map<string, string>();
    for (const client of clientsPage?.items ?? []) options.set(client.id, client.name);
    for (const client of extraClients) if (!options.has(client.id)) options.set(client.id, client.name);
    // The draft's own client may not be on the current search page at all.
    if (invoice?.client && !options.has(invoice.client.id)) options.set(invoice.client.id, invoice.client.name);
    return [...options].map(([value, label]) => ({ value, label }));
  }, [clientsPage, extraClients, invoice]);

  const isReady =
    !!companyId && !isLoadingFirme && !isLoadingFirm && !isLoadingSeries && (!invoiceId || (!isLoadingInvoice && !!invoice));

  // --- Form <-> server synchronization ------------------------------------
  // Ref-guarded exactly like FirmaFormDrawer's: a background refetch (this
  // page invalidates ['invoices'] after every save) must never overwrite what
  // the user is typing.
  useEffect(() => {
    if (!isReady) return;
    const sessionId = invoiceId ?? '__new__';
    if (syncedForRef.current === sessionId) return;

    if (invoice) {
      if (invoice.status !== 'DRAFT') return; // guarded by the redirect below
      form.setFieldsValue({
        seriesId: invoice.seriesId ?? undefined,
        clientId: invoice.clientId,
        dueDate: dayjsFromIsoDate(invoice.dueDate),
        supplyDate: dayjsFromIsoDate(invoice.supplyDate),
        paymentMeansCode: invoice.paymentMeansCode,
        bankAccountId: invoice.bankAccountId ?? undefined,
        notes: invoice.notes ?? undefined,
        lines: invoice.lines.map((line) => ({
          productId: line.productId ?? undefined,
          description: line.description,
          quantity: line.quantity,
          unitCode: line.unitCode,
          unitPrice: line.unitPrice,
          vatCategory: line.vatCategory,
        })),
      });
    } else {
      const defaultSeries = (series ?? []).find((s) => s.docType === 'INVOICE' && s.isActive && s.isDefault);
      const defaultBank = (firmDetail?.bankAccounts ?? []).find((account) => account.isDefault);
      form.setFieldsValue({
        seriesId: defaultSeries?.id,
        paymentMeansCode: '30',
        bankAccountId: defaultBank?.id,
        lines: [emptyLine()],
      });
    }
    syncedForRef.current = sessionId;
    markDirty(false);
  }, [isReady, invoiceId, invoice, series, firmDetail, form, markDirty]);

  // --- Leaving the page with unsaved changes -------------------------------
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) => dirtyRef.current && currentLocation.pathname !== nextLocation.pathname,
  );
  const blockerRef = useRef(blocker);
  blockerRef.current = blocker;

  useEffect(() => {
    if (blocker.state !== 'blocked') return;
    modal.confirm({
      title: 'Modificări nesalvate',
      content: 'Ciorna are modificări care nu au fost salvate. Dacă părăsești pagina, se pierd.',
      okText: 'Părăsește pagina',
      cancelText: 'Rămâi aici',
      okButtonProps: { danger: true },
      onOk: () => blockerRef.current.proceed?.(),
      onCancel: () => blockerRef.current.reset?.(),
    });
    // Only when the blocker actually flips to "blocked" — `blocker` itself is
    // a new object on every render and would re-open the modal endlessly.
  }, [blocker.state, modal]);

  useEffect(() => {
    function handleBeforeUnload(event: BeforeUnloadEvent) {
      if (!dirtyRef.current) return;
      event.preventDefault();
      event.returnValue = '';
    }
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  // --- Switching the current firm while editing ----------------------------
  // Not a navigation, so the blocker above never sees it: the editor simply
  // stops being about the firm the header now says you are in.
  const editorFirmaRef = useRef<string | null>(firmaId);
  useEffect(() => {
    if (!firmaId) return;
    if (editorFirmaRef.current === null) {
      editorFirmaRef.current = firmaId;
      return;
    }
    if (editorFirmaRef.current === firmaId) return;

    const previousFirmaId = editorFirmaRef.current;
    const leave = () => {
      editorFirmaRef.current = firmaId;
      markDirty(false);
      navigate('/facturi', { replace: true });
    };

    if (!dirtyRef.current) {
      leave();
      return;
    }
    modal.confirm({
      title: 'Modificări nesalvate',
      content: 'Ai schimbat firma curentă, iar ciorna are modificări nesalvate. Dacă renunți, modificările se pierd.',
      okText: 'Renunță la modificări',
      cancelText: 'Rămâi pe firma anterioară',
      okButtonProps: { danger: true },
      onOk: leave,
      onCancel: () => setFirmaId(previousFirmaId),
    });
  }, [firmaId, modal, navigate, setFirmaId, markDirty]);

  // --- Mutations -----------------------------------------------------------
  function afterSave(saved: InvoiceDetail) {
    markDirty(false);
    queryClient.setQueryData(['invoices', 'detail', saved.id], saved);
    queryClient.invalidateQueries({ queryKey: ['invoices'] });
    if (!invoiceId) {
      // A created draft gets its own URL, so a refresh (or the browser back
      // button) lands back on it instead of on a blank new invoice.
      syncedForRef.current = saved.id;
      setInvoiceId(saved.id);
      navigate(`/facturi/${saved.id}/editare`, { replace: true });
    }
  }

  const saveMutation = useMutation({
    mutationFn: (values: FacturaFormValues) => {
      const common = {
        clientId: values.clientId as string,
        seriesId: values.seriesId as string,
        supplyDate: values.supplyDate ? values.supplyDate.format('YYYY-MM-DD') : undefined,
        dueDate: values.dueDate ? values.dueDate.format('YYYY-MM-DD') : undefined,
        paymentMeansCode: values.paymentMeansCode ?? '30',
        bankAccountId: values.bankAccountId || undefined,
        // Sent even when empty (unlike the optional fields above): `notes` is
        // `z.string().trim().optional()` server-side, so "" is a valid value
        // and is what lets a user actually CLEAR the note on an existing
        // draft — `undefined` would be dropped from the JSON body and the
        // PATCH would silently keep the old text.
        notes: (values.notes ?? '').trim(),
        lines: (values.lines ?? []).map(toLinePayload),
      };
      return invoiceId
        ? updateFacturaDraft(invoiceId, common as UpdateInvoiceDraftInput)
        : createFacturaDraft({ companyId: companyId as string, currency: 'RON', ...common } as CreateInvoiceDraftInput);
    },
    onSuccess: afterSave,
    onError: (error) => {
      const apiError = getApiError(error);
      if (!applyServerErrors(form, apiError)) message.error(apiError?.message ?? 'Ciorna nu a putut fi salvată.');
    },
  });

  const issueMutation = useMutation({
    mutationFn: (id: string) => issueFactura(id),
    onSuccess: (issued) => {
      markDirty(false);
      message.success(`Factura ${issued.fullNumber ?? ''} a fost emisă.`);
      queryClient.setQueryData(['invoices', 'detail', issued.id], issued);
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      navigate(`/facturi/${issued.id}`, { replace: true });
    },
    onError: (error) => {
      const apiError = getApiError(error);
      if (!applyServerErrors(form, apiError)) message.error(apiError?.message ?? 'Factura nu a putut fi emisă.');
    },
    onSettled: () => setIssueConfirmOpen(false),
  });

  async function handleSaveDraft() {
    try {
      const values = await form.validateFields();
      await saveMutation.mutateAsync(values);
      message.success('Ciorna a fost salvată.');
    } catch {
      // Field errors (client-side rules or the server's, mapped by
      // applyServerErrors) are already rendered on the inputs.
    }
  }

  /** Save first, THEN confirm: the number in the confirmation has to be the
   * number this exact, already-persisted line set will get. */
  async function handleIssueClick() {
    try {
      const values = await form.validateFields();
      const saved = await saveMutation.mutateAsync(values);
      issueTargetRef.current = saved.id;
      setIssueConfirmOpen(true);
    } catch {
      /* already surfaced on the fields */
    }
  }

  if (invoice && invoice.status !== 'DRAFT') {
    return <Navigate to={`/facturi/${invoice.id}`} replace />;
  }

  if (!companyId) {
    return <Empty description="Selectează sau adaugă mai întâi o firmă." />;
  }

  const isSaving = saveMutation.isPending;
  const hasSeries = seriesOptions.length > 0;

  return (
    <div>
      <Space style={{ marginBottom: 16 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/facturi')}>
          Facturi
        </Button>
        <Title level={3} style={{ margin: 0 }}>
          {invoiceId ? 'Editează ciorna' : 'Factură nouă'}
        </Title>
      </Space>

      {/* Both banners assert something about the firm, so neither may render
          before the firm's own data has loaded — an ungated "Neplătitor de
          TVA" is a false statement shown to a VAT payer on every cold load. */}
      {isReady && !hasSeries ? (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message="Firma nu are nicio serie de facturi activă"
          description="Adaugă o serie din pagina Serii înainte de a emite facturi."
        />
      ) : null}

      {isReady && !isVatPayerToday ? (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="Neplătitor de TVA — factura se emite fără TVA."
        />
      ) : null}

      <Spin spinning={!isReady}>
        {/* The Form itself stays mounted at all times so the sync effect's
            `setFieldsValue` always has a connected instance; only its BODY
            waits for `isReady`, because the line table's column set depends
            on the firm's VAT status and must not render (and then re-render)
            with a guessed one. */}
        <Form<FacturaFormValues>
          form={form}
          layout="vertical"
          initialValues={{ lines: [emptyLine()], paymentMeansCode: '30' }}
          onValuesChange={() => markDirty(true)}
        >
          {!isReady ? <div style={{ minHeight: 360 }} /> : null}
          <div style={isReady ? undefined : { display: 'none' }}>
            <Card title="Antet" size="small" style={{ marginBottom: 16 }}>
              <Row gutter={16}>
                <Col xs={24} md={8}>
                  <Form.Item
                    name="seriesId"
                    label="Serie"
                    rules={[{ required: true, message: 'Seria este obligatorie.' }]}
                    extra={preview ? `Următorul număr: ${preview.fullNumber}` : undefined}
                  >
                    <Select options={seriesOptions} placeholder="Alege seria" />
                  </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                  <Form.Item name="dueDate" label="Dată scadență" extra="Gol = se calculează la emitere">
                    <DatePicker format="DD.MM.YYYY" style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                  <Form.Item name="supplyDate" label="Dată livrare">
                    <DatePicker format="DD.MM.YYYY" style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={16}>
                <Col xs={24} md={8}>
                  <Form.Item name="clientId" label="Client" rules={[{ required: true, message: 'Clientul este obligatoriu.' }]}>
                    <Select
                      showSearch
                      filterOption={false}
                      placeholder="Caută clientul"
                      options={clientOptions}
                      onSearch={setClientSearch}
                      popupRender={(menu) => (
                        <>
                          {menu}
                          <Divider style={{ margin: '8px 0' }} />
                          <Button
                            type="link"
                            icon={<PlusOutlined />}
                            style={{ paddingInline: 12 }}
                            onMouseDown={(event) => event.preventDefault()}
                            onClick={() => setClientDrawerOpen(true)}
                          >
                            Client nou
                          </Button>
                        </>
                      )}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                  <Form.Item name="paymentMeansCode" label="Mod plată">
                    <Select options={PAYMENT_MEANS_OPTIONS} />
                  </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                  <Form.Item name="bankAccountId" label="Cont bancar">
                    <Select allowClear options={bankOptions} placeholder="Fără cont bancar" />
                  </Form.Item>
                </Col>
              </Row>
              <Form.Item name="notes" label="Note">
                <Input.TextArea rows={2} placeholder="Mențiuni care apar pe factură" />
              </Form.Item>
            </Card>

            <Card title="Linii" size="small" style={{ marginBottom: 16 }}>
              <LineItemsEditor
                form={form}
                listName="lines"
                products={productsPage?.items ?? []}
                isLoadingProducts={isLoadingProducts}
                vatCategoryOptions={vatCategoryOptions}
                showVat={isVatPayerToday}
                computedLines={computedLines}
              />
            </Card>

            <Row gutter={16} justify="end">
              <Col xs={24} md={10} lg={8}>
                <TotalsPanel totals={liveTotals} showVat={isVatPayerToday} />
              </Col>
            </Row>
          </div>
        </Form>
      </Spin>

      <div
        style={{
          position: 'sticky',
          bottom: 0,
          marginTop: 16,
          padding: '12px 16px',
          background: '#fff',
          borderTop: '1px solid #e8e6df',
          borderRadius: 6,
          boxShadow: '0 -2px 8px rgba(0, 0, 0, 0.04)',
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 8,
          alignItems: 'center',
        }}
      >
        {isDirty ? (
          <Text type="secondary" style={{ marginRight: 'auto' }}>
            Modificări nesalvate
          </Text>
        ) : null}
        {/* The form body is hidden until `isReady`, so neither action can be
            meaningful before then. */}
        <Button onClick={handleSaveDraft} loading={isSaving} disabled={!isReady}>
          Salvează ciorna
        </Button>
        <Popconfirm
          open={issueConfirmOpen}
          title="Emiți factura?"
          description={`Factura va primi numărul ${preview?.fullNumber ?? '…'} și nu va mai putea fi modificată.`}
          okText="Emite factura"
          cancelText="Renunță"
          okButtonProps={{ loading: issueMutation.isPending }}
          onConfirm={() => issueMutation.mutate(issueTargetRef.current as string)}
          onCancel={() => setIssueConfirmOpen(false)}
          // The trigger's own click only SAVES; the confirmation opens from
          // handleIssueClick once the draft is persisted.
          onOpenChange={(open) => {
            if (!open) setIssueConfirmOpen(false);
          }}
        >
          <Button
            type="primary"
            onClick={handleIssueClick}
            loading={isSaving || issueMutation.isPending}
            disabled={!isReady}
          >
            Emite factura
          </Button>
        </Popconfirm>
      </div>

      <ClientFormDrawer
        open={clientDrawerOpen}
        firmaId={companyId}
        editingId={null}
        onClose={() => setClientDrawerOpen(false)}
        onCreated={(client) => {
          setExtraClients((previous) => [client, ...previous]);
          form.setFieldValue('clientId', client.id);
          markDirty(true);
        }}
      />
    </div>
  );
}
